import { FunctionComponent } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import Tooltip from '@mui/material/Tooltip';
import { useFormContext, useWatch } from 'react-hook-form';
import { FaColumns } from 'react-icons/fa';
import { zIndexes } from '../../../../../../styling/zIndexes';
import { cn } from '../../../../../../utilities/cn';
import type {
  FormatOptions,
  LayoutConfig,
} from '../../types/KidPdfGeneratorV2.types';
import { TEXT_LAYOUT_PRESETS } from '../wrapper/BlockInputWrapper';

const arraysEqual = (a?: number[], b?: number[]) => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

const getMatchingPreset = (currentLayout: LayoutConfig | undefined) => {
  if ((currentLayout as LayoutConfig & { custom?: boolean })?.custom) {
    return TEXT_LAYOUT_PRESETS.find((preset) => preset.id === 'custom-ratio');
  }

  const currentFixed = currentLayout?.fixed_width;
  const match = TEXT_LAYOUT_PRESETS.find((preset) => {
    const presetFixed = preset.layout?.fixed_width;
    if (!preset.layout && !currentLayout) return true;
    if (!preset.layout) return false;
    return arraysEqual(currentFixed, presetFixed);
  });

  if (match) return match;
  if (currentFixed && currentFixed.length > 0) {
    return TEXT_LAYOUT_PRESETS.find((preset) => preset.id === 'custom-ratio');
  }
  return TEXT_LAYOUT_PRESETS[0];
};

interface TextBlockLayoutControlsProps {
  formatOptionsPath: string;
  disabled?: boolean;
  compact?: boolean;
}

const TextBlockLayoutControls: FunctionComponent<TextBlockLayoutControlsProps> = ({
  formatOptionsPath,
  disabled = false,
  compact = false,
}) => {
  const { control, setValue } = useFormContext();
  const formatOptions = useWatch({
    name: formatOptionsPath,
    control,
  }) as FormatOptions | undefined;

  const currentLayout = formatOptions?.layout;
  const currentFixedWidth = currentLayout?.fixed_width;

  const setLayout = (nextLayout: LayoutConfig | undefined) => {
    const currentOptions = (formatOptions || {}) as FormatOptions;

    if (!nextLayout) {
      const { layout: _layout, ...rest } = currentOptions;
      const hasOtherOptions = Object.keys(rest).length > 0;
      setValue(formatOptionsPath, hasOtherOptions ? rest : undefined);
      return;
    }

    setValue(formatOptionsPath, {
      ...currentOptions,
      layout: nextLayout,
    });
  };

  const handlePresetSelect = (
    preset: (typeof TEXT_LAYOUT_PRESETS)[number],
  ) => {
    if (preset.id === 'custom-ratio') {
      const next =
        currentFixedWidth && currentFixedWidth.length > 0
          ? {
              ...currentLayout,
              fixed_width: currentFixedWidth,
              custom: true,
            }
          : { fixed_width: [50, 50], custom: true };
      setLayout(next as LayoutConfig);
      return;
    }

    setLayout(preset.layout);
  };

  const handleManualLayoutChange = (colIndex: number, value: number) => {
    if (!currentFixedWidth) return;

    const updated = [...currentFixedWidth];
    const oldValue = updated[colIndex];
    const change = value - oldValue;

    if (change === 0) return;

    updated[colIndex] = value;

    if (updated.length === 1) {
      setLayout(undefined);
      return;
    }

    const otherColumns = updated.filter((_, i) => i !== colIndex);
    const totalOtherColumns = otherColumns.reduce((sum, val) => sum + val, 0);

    if (totalOtherColumns === 0) {
      const remainingColumns = updated.length - 1;
      const evenDistribution = Math.floor((100 - value) / remainingColumns);
      const remainder = (100 - value) % remainingColumns;

      for (let i = 0; i < updated.length; i++) {
        if (i !== colIndex) {
          updated[i] =
            evenDistribution + (i < colIndex + 1 + remainder ? 1 : 0);
        }
      }
    } else {
      const newTotalOther = 100 - value;
      const ratio = newTotalOther / totalOtherColumns;

      for (let i = 0; i < updated.length; i++) {
        if (i !== colIndex) {
          updated[i] = Math.max(1, Math.round(updated[i] * ratio));
        }
      }

      const total = updated.reduce((sum, val) => sum + val, 0);
      const diff = 100 - total;
      if (diff !== 0) {
        for (let i = updated.length - 1; i >= 0; i--) {
          if (i !== colIndex) {
            updated[i] += diff;
            break;
          }
        }
      }
    }

    setLayout({
      ...currentLayout,
      fixed_width: updated,
      custom: true,
    } as LayoutConfig);
  };

  const addColumnRatio = () => {
    if (!currentFixedWidth) return;

    const currentTotal = currentFixedWidth.reduce((sum, val) => sum + val, 0);
    const newColumnValue = Math.max(
      1,
      Math.floor(100 / (currentFixedWidth.length + 1)),
    );

    const updated = [...currentFixedWidth, newColumnValue];
    const remainingSpace = 100 - newColumnValue;
    const existingTotal = currentTotal;

    if (existingTotal > 0) {
      const ratio = remainingSpace / existingTotal;
      for (let i = 0; i < currentFixedWidth.length; i++) {
        updated[i] = Math.max(1, Math.round(updated[i] * ratio));
      }
    }

    const total = updated.reduce((sum, val) => sum + val, 0);
    const diff = 100 - total;
    if (diff !== 0) {
      updated[updated.length - 1] += diff;
    }

    setLayout({
      ...currentLayout,
      fixed_width: updated,
      custom: true,
    } as LayoutConfig);
  };

  const removeColumnRatio = (colIndex: number) => {
    if (!currentFixedWidth || currentFixedWidth.length <= 1) return;

    const removedValue = currentFixedWidth[colIndex];
    const updated = currentFixedWidth.filter((_, i) => i !== colIndex);

    if (updated.length === 1) {
      setLayout(undefined);
      return;
    }

    const remainingTotal = updated.reduce((sum, val) => sum + val, 0);

    if (remainingTotal > 0) {
      const ratio = (remainingTotal + removedValue) / remainingTotal;
      for (let i = 0; i < updated.length; i++) {
        updated[i] = Math.max(1, Math.round(updated[i] * ratio));
      }
    }

    const total = updated.reduce((sum, val) => sum + val, 0);
    const diff = 100 - total;
    if (diff !== 0) {
      updated[updated.length - 1] += diff;
    }

    setLayout({
      ...currentLayout,
      fixed_width: updated,
      custom: true,
    } as LayoutConfig);
  };

  const selectedPreset = getMatchingPreset(currentLayout) || TEXT_LAYOUT_PRESETS[0];
  const TriggerIcon = selectedPreset?.icon || FaColumns;
  const label = selectedPreset?.name || 'Layout';
  const iconSize = compact ? 14 : 18;
  const hasLayout = Boolean(currentLayout?.fixed_width?.length);

  return (
    <Popover className="relative">
      <Tooltip
        title={`Layout: ${label}`}
        arrow
        placement="bottom"
        slotProps={{
          arrow: {
            className: 'text-mainblue',
          },
        }}
      >
        <span className="inline-flex">
          <PopoverButton
            disabled={disabled}
            className={cn(
              'border transition-colors focus:outline-none',
              compact
                ? 'flex h-7 w-7 shrink-0 items-center justify-center rounded-md'
                : 'rounded p-2',
              disabled
                ? 'cursor-not-allowed border-gray-200 text-gray-400'
                : hasLayout
                  ? compact
                    ? 'border-blue-400 bg-blue-50 text-blue-700'
                    : 'border-blue-400 bg-blue-50 text-gray-600'
                  : 'border-gray-200 text-gray-600 hover:border-blue-500 hover:bg-gray-100 hover:text-blue-600',
            )}
            aria-label={`Layout presets - ${label}`}
          >
            <TriggerIcon size={iconSize} />
          </PopoverButton>
        </span>
      </Tooltip>
      <PopoverPanel
        anchor={{ to: 'bottom start', gap: 8, padding: 16 }}
        style={{ zIndex: zIndexes.floatingPanel }}
        className="rounded-md border border-gray-200 bg-white p-2 shadow-xl [--anchor-max-height:none]"
      >
        <div className="flex items-center gap-1">
          {TEXT_LAYOUT_PRESETS.map((preset) => {
            const IconComponent = preset.icon;
            const isSelected = getMatchingPreset(currentLayout)?.name === preset.name;
            return (
              <Tooltip
                key={preset.name}
                title={preset.name}
                arrow
                placement="bottom"
              >
                <button
                  type="button"
                  onClick={() => handlePresetSelect(preset)}
                  className={`flex items-center rounded-md border p-2 text-sm transition-colors ${isSelected
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300 hover:bg-gray-100'
                    }`}
                  aria-label={preset.name}
                >
                  <IconComponent className="text-base" />
                </button>
              </Tooltip>
            );
          })}
        </div>
        {getMatchingPreset(currentLayout)?.id === 'custom-ratio' &&
          currentFixedWidth &&
          currentFixedWidth.length > 0 && (
            <div className="mt-2 border-t border-gray-200 pt-2">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-600">
                  Custom ratio
                </span>
                <button
                  type="button"
                  className="rounded bg-blue-600 px-2 py-0.5 text-xs text-white"
                  onClick={addColumnRatio}
                  title="Add column"
                >
                  +
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {currentFixedWidth.map((val, colIndex) => (
                  <div key={colIndex} className="flex items-center gap-1">
                    <input
                      type="number"
                      min={1}
                      max={100}
                      step={1}
                      className="w-14 rounded border border-gray-300 p-1 text-[11px]"
                      value={val}
                      onChange={(e) => {
                        const newValue = Math.max(
                          1,
                          Math.min(100, Number(e.target.value) || 1),
                        );
                        handleManualLayoutChange(colIndex, newValue);
                      }}
                    />
                    <span className="text-[11px] text-gray-500">%</span>
                    {currentFixedWidth.length > 1 && (
                      <button
                        type="button"
                        className="rounded px-1 text-[11px] text-red-600 hover:bg-red-50"
                        title="Remove column"
                        onClick={() => removeColumnRatio(colIndex)}
                      >
                        ✖
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
      </PopoverPanel>
    </Popover>
  );
};

export default TextBlockLayoutControls;
